import { useCallback, useEffect, useMemo } from 'react';
import _ from 'lodash';

import { HealthPlanRenewal } from '@/modules/Renewals/renewals.types';
import useHealthPlansStore from '@/modules/Renewals/RenewalSetup/stores/useHealthPlans.store';

export const EMPTY_FIELD_ERROR_MESSAGE = "Can't be empty";
export const UNIQUE_PLAN_CODE_ERROR = 'Must be unique';

export const HealthPlanField = {
  PlanCode: 'planCode',
  Name: 'name',
} as const;

export type HealthPlanFieldType = (typeof HealthPlanField)[keyof typeof HealthPlanField];

type DefaultFieldKey = 'defaultPlanCode' | 'defaultName';
const DEFAULT_KEY_BY_FIELD: Record<HealthPlanFieldType, DefaultFieldKey> = {
  planCode: 'defaultPlanCode',
  name: 'defaultName',
} as const;

export const validateHealthPlans = (healthPlans: HealthPlanRenewal[], serverInvalidCodes: string[] = []) => {
  const errors: Record<string, Partial<Record<HealthPlanFieldType, string[]>>> = {};

  healthPlans.forEach((hp) => {
    const id = hp.id || '';
    [HealthPlanField.Name, HealthPlanField.PlanCode].forEach((field) => {
      const value = hp[field];
      if (!value || value.trim().length === 0) {
        errors[id] = {
          ...(errors[id] || {}),
          [field]: [EMPTY_FIELD_ERROR_MESSAGE],
        };
      }
    });
  });

  const codeCount: Record<string, string[]> = {};
  healthPlans.forEach((hp) => {
    const code = (hp.planCode || '').trim();
    if (!code) return;
    if (!codeCount[code]) codeCount[code] = [];
    codeCount[code].push(hp.id || '');
  });
  const duplicates = Object.values(codeCount)
    .filter((ids) => ids.length > 1)
    .flat();

  const serverInvalidIds = healthPlans
    .filter((hp) => serverInvalidCodes.includes(hp.planCode || ''))
    .map((hp) => hp.id || '');

  [...duplicates, ...serverInvalidIds].forEach((id) => {
    errors[id] = {
      ...(errors[id] || {}),
      planCode: [
        ...(errors[id]?.planCode?.filter((msg) => msg !== UNIQUE_PLAN_CODE_ERROR) || []),
        UNIQUE_PLAN_CODE_ERROR,
      ],
    };
  });

  return errors;
};

export const useHealthPlanCodeActions = () => {
  const setState = useHealthPlansStore((state) => state.setState);

  const updateEntity = useCallback(
    (id: string, field: HealthPlanFieldType, value: string) => {
      setState((prev) => {
        const healthPlans = (prev.healthPlans || []).map((hp) =>
          hp.id === id ? { ...hp, [field]: value } : hp
        );
        return { ...prev, healthPlans };
      });
    },
    [setState]
  );

  const updateEntityDebounced = useMemo(
    () =>
      _.debounce((id: string, field: HealthPlanFieldType, value: string) => {
        updateEntity(id, field, value);
      }, 250),
    [updateEntity]
  );

  const resetEntity = useCallback(
    (id: string, field: HealthPlanFieldType) => {
      setState((prev) => {
        const healthPlans = (prev.healthPlans || []).map((hp) =>
          hp.id === id ? { ...hp, [field]: hp[DEFAULT_KEY_BY_FIELD[field]] || '' } : hp
        );
        return { ...prev, healthPlans };
      });
    },
    [setState]
  );

  const setFieldErrors = useCallback(
    (id: string, field: HealthPlanFieldType, messages: string | string[]) => {
      const list = Array.isArray(messages) ? messages : [messages];
      setState((prev) => ({
        ...prev,
        errors: {
          ...prev.errors,
          [id]: { ...(prev.errors?.[id] || {}), [field]: list },
        },
      }));
    },
    [setState]
  );

  const clearErrors = useCallback(() => {
    setState((prev) => ({ ...prev, errors: {} }));
  }, [setState]);

  useEffect(() => () => updateEntityDebounced.cancel(), [updateEntityDebounced]);

  return { updateEntity, updateEntityDebounced, resetEntity, setFieldErrors, clearErrors };
};

export const useHealthPlanFieldError = (healthPlanId?: string, field?: HealthPlanFieldType) =>
  useHealthPlansStore(
    useCallback(
      (state) => (healthPlanId && field ? state.state.errors?.[healthPlanId]?.[field]?.[0] : undefined),
      [healthPlanId, field]
    )
  );

export const useEditableHealthPlan = (healthPlanId?: string) =>
  useHealthPlansStore(
    useCallback(
      (state) =>
        healthPlanId
          ? (state.state.healthPlans || []).find((hp: HealthPlanRenewal) => hp.id === healthPlanId)
          : undefined,
      [healthPlanId]
    )
  );
